/**
 * Besichtigung aus dem Gespräch heraus planen (Benjamin, 03.10.2026).
 *
 * Der Kunde schreibt „Dienstag, 06.10., 11:00 Uhr, Wolfszug 18 in 01157
 * Dresden" — daraus wird mit einem Blatt ein Besichtigungstermin im Planer.
 * Tag, Uhrzeit, Adresse und Telefon sucht lib/terminAusMail.ausVerlauf im
 * ganzen Verlauf (Mails des Kunden, neueste zuerst); alles bleibt änderbar.
 * Ist jemand schon eingeplant, fragt das Konflikt-Blatt, wie die Zeit
 * aufgeteilt wird (lib/terminKonflikt.ts).
 *
 * Bei einem Leed geschieht alles in einem Schritt: das Leed wird Kunde (wie
 * „Als Kunde übernehmen"), dann steht der Termin. Ein Termin braucht einen
 * Kunden, sonst taucht er nirgends wieder auf (planer.planung_create).
 * Weicht die Adresse bei einem bestehenden Kunden ab, wird sie seine
 * Lieferadresse (lib/terminAusMail.adressPlan) — der Termin-Ort kommt immer aus
 * hinterlegten Adressen, nie aus freiem Text.
 */
import { useCallback, useEffect, useState } from "react";

import { ApiError, fehlerText } from "@vimaana/ui";

import type { Faden } from "../../hooks/useNachrichten";
import { useCreateTermin, useKundeDetail, useTeamOwners, useUpdateKunde } from "../../hooks/usePlanung";
import type { NewTerminBody } from "../../hooks/usePlanung";
import { useLeedKandidaten, useLeedPatch, useLeedUmwandeln, useMailSignaturen, type LeedKandidat } from "../../hooks/usePost";
import { neuerKundeName, useTerminVorschlaege, useVerlaufFund } from "../../hooks/useVerlauf";
import { kundenName } from "../../lib/anrede";
import {
  adressPlan, adresszeile, bestaetigungText, eigeneBegruessung, tagLang, zeitPlus,
} from "../../lib/terminAusMail";
import { mitAntwort, mitKonflikten } from "../../lib/terminKonflikt";
import AnredeFeld from "../kunden/AnredeFeld";
import { useKonfliktFrage } from "../planer/KonfliktFrage";
import KundenKandidaten, { type MailWahl } from "./KundenKandidaten";

/** Eine Besichtigung dauert eine halbe Stunde (Benjamin, 04.10.2026) — änderbar. */
const DAUER_MIN = 30;

function minuten(von: string, bis: string): number {
  const m = (z: string) => { const [h, mi] = z.split(":").map(Number); return h * 60 + mi; };
  return /^\d{2}:\d{2}$/.test(von) && /^\d{2}:\d{2}$/.test(bis) ? m(bis) - m(von) : DAUER_MIN;
}

export type BesichtigungErgebnis = { kundeId: string; datum: string; bestaetigung: string };
type Props = { f: Faden; onClose: () => void; onFertig: (e: BesichtigungErgebnis) => void; onPlaner: (datum: string) => void };

export default function BesichtigungPlanen({ f, onClose, onFertig, onPlaner }: Props) {
  const leed = f.art === "leed" ? f.leed : null;
  const vorschlag = useVerlaufFund(f);
  const termine = useTerminVorschlaege(f);
  const { dialog: konfliktDialog, fragen } = useKonfliktFrage();
  const [zielKunde, setZielKunde] = useState(""); // Leed, das zu einem Kunden im Stamm gehört
  const [mailWahl, setMailWahl] = useState<MailWahl>(""); // was mit der Mail des Leeds beim Zuordnen geschieht
  const kundeId = f.kunde_id || zielKunde;
  const kunde = useKundeDetail(kundeId || undefined).data;
  const owners = useTeamOwners().data || [];
  const ich = useMailSignaturen().data?.postfaecher?.find((p) => p.eigenes)?.owner_id || "";

  // Der zuletzt im Verlauf genannte Termin ist vorgewählt, die anderen stehen als Knöpfe darunter.
  const [datum, setDatum] = useState(termine[0]?.datum || "");
  const [von, setVon] = useState(termine[0]?.zeit || "");
  const [bis, setBis] = useState(termine[0]?.zeit ? zeitPlus(termine[0].zeit, DAUER_MIN) : "");
  const vonAendern = (v: string) => {
    const dauer = minuten(von, bis);
    setVon(v);
    if (/^\d{2}:\d{2}$/.test(v)) setBis(zeitPlus(v, dauer > 0 ? dauer : DAUER_MIN));
  };
  const angebotWaehlen = (t: { datum: string; zeit: string }) => {
    setDatum(t.datum);
    if (t.zeit) { setVon(t.zeit); setBis(zeitPlus(t.zeit, minuten(von, bis) > 0 ? minuten(von, bis) : DAUER_MIN)); }
  };
  const mitAdresse = !!vorschlag.strasse;
  const [adr, setAdr] = useState({
    strasse: mitAdresse ? vorschlag.strasse : leed?.strasse || "",
    plz: mitAdresse ? vorschlag.plz : leed?.plz || "",
    ort: mitAdresse ? vorschlag.ort : leed?.ort || "",
  });
  // Privatkunde: Anrede, Vor- und Nachname wie in der Kundenmaske; Firma: ein Name.
  const privat = (leed?.kundentyp || "privat") !== "firma";
  const [person, setPerson] = useState(() => neuerKundeName(f, leed?.name || "", vorschlag));
  const [firmaName, setFirmaName] = useState(leed?.name || "");
  const name = privat ? kundenName(person.anrede, person.vorname, person.nachname) : firmaName;
  const [telefon, setTelefon] = useState(leed?.telefon || vorschlag.telefon || "");
  const [notiz, setNotiz] = useState((leed?.anliegen || "").trim().slice(0, 400));
  const [wer, setWer] = useState<string[]>([]);
  // Steht der Absender schon im Stamm? Vorab gesucht, beim Anlegen vom Server nachgereicht (409).
  const kandidatenQ = useLeedKandidaten(leed && !f.kunde_id ? leed.id : undefined);
  const [nachgereicht, setNachgereicht] = useState<LeedKandidat[]>([]);
  const kandidaten = nachgereicht.length ? nachgereicht : kandidatenQ.data ?? [];
  const hart = kandidaten.some((k) => k.hart);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [fertig, setFertig] = useState<BesichtigungErgebnis | null>(null);
  // Das Leed ist schon Kunde, der Termin fehlt noch (Konfliktfrage abgebrochen, Fehler beim Anlegen).
  const [uebernommen, setUebernommen] = useState("");
  const schliessen = useCallback(() => {
    if (uebernommen && !fertig) onFertig({ kundeId: uebernommen, datum: "", bestaetigung: "" });
    else onClose();
  }, [uebernommen, fertig, onFertig, onClose]);

  const leedPatch = useLeedPatch();
  const umwandeln = useLeedUmwandeln();
  const kundeAendern = useUpdateKunde();
  const anlegen = useCreateTermin();

  // Bestehender Kunde ohne Adresse in der Mail: seine Adresse zeigen.
  useEffect(() => {
    if (kunde && !mitAdresse && !leed) {
      setAdr((v) => (v.strasse || v.plz || v.ort ? v : { strasse: kunde.strasse || "", plz: kunde.plz || "", ort: kunde.ort || "" }));
    }
  }, [kunde, mitAdresse, leed]);
  useEffect(() => { if (ich && !wer.length) setWer([ich]); }, [ich]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const zu = (e: KeyboardEvent) => { if (e.key === "Escape" && !laeuft) schliessen(); };
    window.addEventListener("keydown", zu);
    return () => window.removeEventListener("keydown", zu);
  }, [schliessen, laeuft]);

  const plan = kundeId ? adressPlan(kunde, adr) : { patch: {}, hinweis: "" };
  const zeitOk = /^\d{2}:\d{2}$/.test(von) && /^\d{2}:\d{2}$/.test(bis) && von < bis;
  const bereit = !!datum && zeitOk && (!leed || !!name.trim() || !!zielKunde) && (!!kundeId || !!leed) && !laeuft
    && !(leed && hart && !zielKunde) && !(leed && !zielKunde && kandidatenQ.isPending) && (!kundeId || !!kunde);

  const los = async () => {
    setLaeuft(true); setFehler(null);
    try {
      let kid = kundeId || uebernommen;
      if (leed && !uebernommen) {
        if (!zielKunde) {
          const felder: Record<string, string> = {};
          const neu: Record<string, string> = { name, telefon, ...adr };
          for (const [k, v] of Object.entries(neu)) if (v.trim() !== ((leed as Record<string, string>)[k] || "").trim()) felder[k] = v.trim();
          if (Object.keys(felder).length) await leedPatch.mutateAsync({ id: leed.id, felder });
        }
        try {
          const r = await umwandeln.mutateAsync({ id: leed.id, body: {
            kunde_id: zielKunde, projekt_titel: "", projekt_ort: "", projektart: "",
            mail: zielKunde ? mailWahl : "", trotzdem_neu: !zielKunde && kandidaten.length > 0,
          } });
          kid = r.kunde_id;
          setUebernommen(r.kunde_id);
          // Die Anrede kennt das Leed nicht — sie geht gleich in den neuen Kundenstamm.
          if (privat && !zielKunde) {
            await kundeAendern.mutateAsync({ id: r.kunde_id, patch: { ...person, name } });
          }
        } catch (e) {
          // 409 = der Server kennt passende Kunden: gleiche Mail sperrt, Ähnliches fragt nach.
          const d = e instanceof ApiError ? (e.body as { detail?: { kunden?: LeedKandidat[] } })?.detail : null;
          if (d && typeof d === "object" && Array.isArray(d.kunden)) { setNachgereicht(d.kunden); return; }
          throw e;
        }
      }
      if (!kid) throw new Error("Kein Kunde — der Termin hätte keinen Bezug.");
      if (Object.keys(plan.patch).length && (!leed || zielKunde) && !uebernommen) await kundeAendern.mutateAsync({ id: kid, patch: plan.patch });
      const body: NewTerminBody = {
        projekt_id: "", kunde_id: kid, planung_typ: "besichtigung", title: "Besichtigung",
        start_at: `${datum}T${von}:00`, end_at: `${datum}T${bis}:00`, notiz: notiz.trim(),
        benoetigte_helfer: 0, offene_stellen: 0,
        slots: wer.map((id) => ({
          person_id: id, person_typ: "inhaber" as const, start_at: `${datum}T${von}:00`, end_at: `${datum}T${bis}:00`,
          pause_stunden: 0, stundenlohn: 0, zusatzkosten: 0, note: "", verguetungsart: "stundenlohn" as const,
          pauschale_id: null, einladungs_status: "zugesagt",
        })),
      };
      // Doppelbelegung: fragen, wie die Zeit aufgeteilt wird. Abbrechen = nichts angelegt.
      const neu = await mitKonflikten((a) => anlegen.mutateAsync(mitAntwort(body, a)), fragen);
      if (!neu) return;
      const eigene = f.nachrichten.filter((n) => n.richtung === "out").map((n) => n.voll || n.text);
      setFertig({
        kundeId: kid, datum,
        bestaetigung: bestaetigungText({ begruessung: eigeneBegruessung(eigene), name: leed ? name.trim() : kunde?.name || f.titel, datum, von, adresse: adresszeile(adr) }),
      });
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(false);
    }
  };

  const umschalten = (id: string) => setWer((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));
  const gelesen = !!(termine.length || vorschlag.strasse || (vorschlag.telefon && !leed?.telefon));

  return (
    <div className="nb-dialog-schleier" onMouseDown={(e) => { if (e.target === e.currentTarget && !laeuft) schliessen(); }}>
      <section className="nb-dialog" role="dialog" aria-modal="true" aria-label="Besichtigung planen">
        <header className="nb-dialog-kopf">
          <h3>Besichtigung planen</h3>
          <button type="button" className="nb-zu" aria-label="Schließen" onClick={schliessen} disabled={laeuft}>×</button>
        </header>

        {fertig ? (
          <div className="nb-dialog-inhalt">
            <p>
              Die Besichtigung steht im Planer: {tagLang(datum)} {von}–{bis} Uhr
              {leed ? (zielKunde ? ` — zugeordnet zu ${kunde?.name || "dem bestehenden Kunden"}, neu angelegt wurde niemand` : `, ${name.trim() || "der Kunde"} ist neu im Kundenstamm`) : ""}.
            </p>
            <p className="nb-dialog-mail">Die Bestätigung steht im Antwortfeld — lesen, anpassen, senden.</p>
            <div className="nb-dialog-knoepfe">
              <button type="button" className="vg-btn" onClick={() => { onFertig(fertig); onPlaner(datum); }}>Im Planer zeigen</button>
              <button type="button" className="vg-btn vg-btn-primary" onClick={() => onFertig(fertig)}>Zur Antwort</button>
            </div>
          </div>
        ) : (
          <div className="nb-dialog-inhalt">
            {gelesen && <p className="nb-dialog-mail">Clara hat das aus dem Mailverlauf gelesen — bitte prüfen.</p>}
            <div className="nb-termin-felder">
              <label className="nb-feld ist-datum"><span>Tag</span>
                <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
              </label>
              <label className="nb-feld"><span>Von</span>
                <input type="time" step={900} value={von} onChange={(e) => vonAendern(e.target.value)} />
              </label>
              <label className="nb-feld"><span>Bis</span>
                <input type="time" step={900} value={bis} onChange={(e) => setBis(e.target.value)} />
              </label>
            </div>
            {datum && <p className="nb-termin-tag">{tagLang(datum)}{von && !zeitOk ? " — „Bis“ muss nach „Von“ liegen" : ""}</p>}
            {termine.length > 1 && (
              <div className="nb-angebote" role="group" aria-label="Im Verlauf genannte Termine">
                {termine.slice(0, 4).map((t) => {
                  const an = t.datum === datum && (!t.zeit || t.zeit === von);
                  return (
                    <button key={`${t.datum}-${t.zeit}`} type="button" className={"nb-angebot" + (an ? " ist-an" : "")}
                      aria-pressed={an} onClick={() => angebotWaehlen(t)}
                      title={`Genannt ${t.von_uns ? "von uns" : "vom Kunden"} am ${new Date(t.zeitpunkt).toLocaleDateString("de-DE")}`}>
                      {new Date(`${t.datum}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}{t.zeit ? ` · ${t.zeit}` : ""}
                    </button>
                  );
                })}
              </div>
            )}

            {uebernommen && <p className="nb-termin-hinweis">{name.trim() || "Der Kunde"} ist übernommen — es fehlt nur noch der Termin.</p>}
            {leed && !uebernommen && !zielKunde && (
              <KundenKandidaten kandidaten={kandidaten} leedMail={leed.email} disabled={laeuft}
                onZuordnen={(k, m) => { setZielKunde(k.id); setMailWahl(m); }} />
            )}
            {leed && !uebernommen && zielKunde && (
              <p className="nb-termin-hinweis">
                Wird {kunde?.name || "dem Kunden"} zugeordnet{mailWahl === "ersetzen" ? `, die Mailadresse wird ${leed.email}` : mailWahl === "dazu" ? `, ${leed.email} kommt als weitere Adresse dazu` : ""}.{" "}
                <button type="button" className="vg-btn vg-btn-ghost" onClick={() => { setZielKunde(""); setMailWahl(""); }} disabled={laeuft}>Ändern</button>
              </p>
            )}
            {leed && !zielKunde && !uebernommen && !hart && kandidaten.length > 0 && <p className="nb-dialog-zwischen">Nicht dabei? Dann neu anlegen:</p>}
            {leed && !zielKunde && !uebernommen && !hart && (
              <div className="nb-dialog-felder">
                {privat ? (
                  <div className="nb-feld ist-name"><AnredeFeld {...person} onChange={setPerson} /></div>
                ) : (
                  <label className="nb-feld ist-name"><span>Firma (wird Kunde)</span>
                    <input type="text" value={firmaName} onChange={(e) => setFirmaName(e.target.value)} />
                  </label>
                )}
                <label className="nb-feld ist-telefon"><span>Telefon</span>
                  <input type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} />
                </label>
              </div>
            )}
            <div className="nb-dialog-felder">
              <label className="nb-feld ist-strasse"><span>Straße</span>
                <input type="text" value={adr.strasse} onChange={(e) => setAdr({ ...adr, strasse: e.target.value })} />
              </label>
              <label className="nb-feld"><span>PLZ</span>
                <input type="text" inputMode="numeric" value={adr.plz} onChange={(e) => setAdr({ ...adr, plz: e.target.value })} />
              </label>
              <label className="nb-feld"><span>Ort</span>
                <input type="text" value={adr.ort} onChange={(e) => setAdr({ ...adr, ort: e.target.value })} />
              </label>
            </div>
            {plan.hinweis && <p className="nb-termin-hinweis">{plan.hinweis}</p>}

            {owners.length > 0 && (
              <div className="nb-wer" role="group" aria-label="Wer fährt hin">
                <span className="nb-wer-titel">Wer fährt hin</span>
                {owners.filter((o) => o.active !== false).map((o) => (
                  <button key={o.id} type="button" className={"nb-wer-knopf" + (wer.includes(o.id) ? " ist-an" : "")} aria-pressed={wer.includes(o.id)} onClick={() => umschalten(o.id)}>
                    {wer.includes(o.id) ? "✓ " : ""}{o.name.split(" ")[0]}
                  </button>
                ))}
              </div>
            )}
            <label className="nb-feld"><span>Notiz</span>
              <textarea rows={3} value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Worum geht es? z. B. Hecke, Teich, Baumfällung" />
            </label>

            {!!fehler && <p className="vg-post-fehler">{fehlerText(fehler)}</p>}
            <div className="nb-dialog-knoepfe">
              <button type="button" className="vg-btn vg-btn-ghost" onClick={schliessen} disabled={laeuft}>Abbrechen</button>
              <button type="button" className="vg-btn vg-btn-primary" disabled={!bereit} onClick={los}>
                {laeuft ? "Wird angelegt …" : leed && !zielKunde && !uebernommen ? (kandidaten.length ? "Neu anlegen und Besichtigung planen" : "Kunde übernehmen und Besichtigung anlegen") : "Besichtigung anlegen"}
              </button>
            </div>
          </div>
        )}
      </section>
      {konfliktDialog}
    </div>
  );
}
